'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchCarouselCities } from '@/app/actions'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'

type CarouselCity = {
  id: number
  primary_city: string
  primary_state: string
  hero_image_url: string
  median_home_price: number | null
  rank_population: number | null
}

const CAROUSEL_LIMIT = 13
const EAGER_LOAD_COUNT = 3
const IMAGE_TIMEOUT_MS = 8000

// Top military-connected cities to feature — keeps the carousel
// fast by only loading a fixed set rather than the whole table.
const FEATURED_CITIES = [
  'San Diego', 'San Antonio', 'Virginia Beach', 'Fayetteville',
  'Colorado Springs', 'Jacksonville', 'Honolulu', 'Killeen',
  'Norfolk', 'Tacoma', 'Tampa', 'Clarksville', 'Pensacola',
]

const STATE_GRADIENTS: Record<string, string> = {
  AL: 'from-red-800 to-red-600',
  AK: 'from-blue-900 to-blue-700',
  AZ: 'from-teal-700 to-cyan-500',
  CA: 'from-sky-700 to-blue-500',
  CO: 'from-blue-700 to-sky-500',
  FL: 'from-cyan-600 to-teal-500',
  GA: 'from-red-700 to-rose-500',
  HI: 'from-cyan-700 to-teal-500',
  NC: 'from-blue-800 to-blue-500',
  TX: 'from-red-900 to-red-600',
  VA: 'from-blue-800 to-blue-500',
  WA: 'from-emerald-800 to-emerald-500',
  DEFAULT: 'from-slate-700 to-slate-500',
}

const formatPopulation = (value: number | null) =>
  value ? value.toLocaleString() : 'N/A'

const formatPrice = (value: number | null) => {
  if (!value) return null
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}m`
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`
  return `$${value}`
}

const shuffle = (list: CarouselCity[]) => {
  const copy = [...list]
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = copy[i]
    copy[i] = copy[j]
    copy[j] = temp
  }
  return copy
}

const optimizeImageUrl = (url: string, options?: { width: number; quality: number }) => {
  if (!url) return url
  try {
    const parsed = new URL(url)
    const marker = '/storage/v1/object/public/'
    if (parsed.pathname.includes(marker)) {
      parsed.pathname = parsed.pathname.replace(
        marker,
        '/storage/v1/render/image/public/'
      )
      parsed.searchParams.set('width', String(options?.width ?? 720))
      parsed.searchParams.set('quality', String(options?.quality ?? 70))
      return parsed.toString()
    }
  } catch {
    return url
  }
  return url
}

function CityCard({ city, index }: { city: CarouselCity; index: number }) {
  const [imgStatus, setImgStatus] = useState<'loading' | 'loaded' | 'fallback'>('loading')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (imgStatus === 'loading') {
      timeoutRef.current = setTimeout(() => {
        setImgStatus((s) => (s === 'loading' ? 'fallback' : s))
      }, IMAGE_TIMEOUT_MS)
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [imgStatus])

  const handleLoad = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setImgStatus('loaded')
  }, [])

  const handleError = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setImgStatus('fallback')
  }, [])

  const priceTag = formatPrice(city.median_home_price)
  const stateAbbrev = city.primary_state?.slice(0, 2) ?? ''
  const gradient = STATE_GRADIENTS[stateAbbrev] ?? STATE_GRADIENTS.DEFAULT

  return (
    <div
      data-index={index}
      className="relative min-w-[240px] overflow-hidden rounded-2xl border border-[var(--border)] shadow-sm transition hover:shadow-md dark:shadow-none sm:min-w-[280px]"
    >
      {/* Shimmer placeholder — visible while image loads */}
      {imgStatus === 'loading' && (
        <div className="absolute inset-0 z-10">
          <div className={`h-full w-full bg-gradient-to-br ${gradient} opacity-60`} />
          <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>
      )}

      {/* Gradient fallback — shown when image fails or times out */}
      {imgStatus === 'fallback' && (
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
      )}

      {/* Actual image — hidden when fallback, transparent while loading */}
      {imgStatus !== 'fallback' && (
        <img
          src={encodeURI(city.hero_image_url)}
          alt={`${city.primary_city} skyline`}
          className={`h-48 w-full object-cover transition-opacity duration-500 ${
            imgStatus === 'loaded' ? 'opacity-100' : 'opacity-0'
          }`}
          referrerPolicy="no-referrer"
          loading={index < EAGER_LOAD_COUNT ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={index < EAGER_LOAD_COUNT ? 'high' : 'low'}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {/* Ensure card has height even in fallback */}
      {imgStatus === 'fallback' && <div className="h-48 w-full" />}

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      {priceTag && (
        <span className="absolute left-3 top-3 z-20 rounded-full border border-white/20 bg-black/50 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md">
          {priceTag} median
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
        <h3 className="text-base font-semibold text-white">
          {city.primary_city}, {city.primary_state}
        </h3>
        <p className="text-xs text-white/80">
          Population {formatPopulation(city.rank_population)}
        </p>
      </div>
    </div>
  )
}

export default function LandingCityCarousel() {
  const supabase = useMemo(() => createBrowserSupabaseClient(), [])
  const [cities, setCities] = useState<CarouselCity[]>([])
  const [loading, setLoading] = useState(true)
  const [paused, setPaused] = useState(false)
  const [isInView, setIsInView] = useState(true)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const loopPointRef = useRef(0)
  const animationRef = useRef<number | null>(null)
  const resumeTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    let mounted = true
    const loadCities = async () => {
      setLoading(true)
      try {
        const { data } = await supabase.auth.getSession()
        const accessToken = data.session?.access_token
        const connection = (
          navigator as Navigator & {
            connection?: { saveData?: boolean; effectiveType?: string }
          }
        ).connection
        const slowNetwork =
          connection?.saveData || Boolean(connection?.effectiveType?.includes('2g'))
        const imageOptions = slowNetwork
          ? { width: 560, quality: 60 }
          : { width: 720, quality: 70 }
        // Fetch enough rows to find our featured cities
        const rows = await fetchCarouselCities({ accessToken, limit: 200 })
        const normalized = rows
          .map((row) => {
            const economics = Array.isArray(row.city_economics)
              ? row.city_economics[0]
              : row.city_economics
            return {
              id: row.city_id,
              primary_city: row.primary_city,
              primary_state: row.primary_state ? row.primary_state.trim().toUpperCase() : '',
              hero_image_url: row.hero_image_url ?? '',
              median_home_price: economics?.median_home_price ?? null,
              rank_population: row.rank_population ?? null
            }
          })
          .filter((city) => city.hero_image_url)

        // Only keep the curated list of top military cities
        const featuredSet = new Set(FEATURED_CITIES.map((c) => c.toLowerCase()))
        const featured = normalized.filter((city) =>
          featuredSet.has(city.primary_city.toLowerCase())
        )

        if (mounted) {
          const limited = featured.slice(0, CAROUSEL_LIMIT).map((city) => ({
            ...city,
            hero_image_url: optimizeImageUrl(city.hero_image_url, imageOptions)
          }))
          setCities(limited)
        }
      } catch {
        if (mounted) setCities([])
      }
      if (mounted) setLoading(false)
    }

    loadCities()

    return () => {
      mounted = false
    }
  }, [supabase])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || cities.length === 0) return undefined
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion || paused || !isInView) return undefined

    let lastTime = performance.now()
    const speed = 0.04
    const updateLoopPoint = () => {
      const loopPoint = scroller.scrollWidth / 2
      loopPointRef.current = Number.isFinite(loopPoint) && loopPoint > 0 ? loopPoint : 0
    }

    const handleResize = () => {
      updateLoopPoint()
    }

    updateLoopPoint()
    window.addEventListener('resize', handleResize)

    const step = (time: number) => {
      const delta = time - lastTime
      lastTime = time
      scroller.scrollLeft += delta * speed
      const loopPoint = loopPointRef.current
      if (loopPoint > 0) {
        if (scroller.scrollLeft >= loopPoint) {
          scroller.scrollLeft -= loopPoint
        }
        if (scroller.scrollLeft < 0) {
          scroller.scrollLeft += loopPoint
        }
      }
      animationRef.current = window.requestAnimationFrame(step)
    }

    animationRef.current = window.requestAnimationFrame(step)
    return () => {
      if (animationRef.current) window.cancelAnimationFrame(animationRef.current)
      window.removeEventListener('resize', handleResize)
    }
  }, [cities.length, isInView, paused])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting)
      },
      { rootMargin: '200px' }
    )
    observer.observe(scroller)
    return () => observer.disconnect()
  }, [])

  const handlePause = () => {
    setPaused(true)
    if (resumeTimeoutRef.current) window.clearTimeout(resumeTimeoutRef.current)
  }

  const handleResume = () => {
    if (resumeTimeoutRef.current) window.clearTimeout(resumeTimeoutRef.current)
    resumeTimeoutRef.current = window.setTimeout(() => setPaused(false), 1200)
  }

  if (loading) {
    return (
      <div className="flex gap-4 overflow-hidden">
        {[0, 1, 2].map((slot) => (
          <div
            key={slot}
            className="min-w-[240px] rounded-2xl border border-[var(--border)] bg-[var(--surface-1)]/80 p-4"
          >
            <div className="h-36 w-full rounded-xl bg-[var(--surface-2)]" />
            <div className="mt-3 h-4 w-2/3 rounded bg-[var(--surface-2)]" />
            <div className="mt-2 h-3 w-1/2 rounded bg-[var(--surface-2)]" />
          </div>
        ))}
      </div>
    )
  }

  if (cities.length === 0) return null

  const loopCities = cities.length > 0 ? [...cities, ...cities] : cities

  return (
    <div className="space-y-3">
      <div
        ref={scrollerRef}
        onMouseEnter={handlePause}
        onMouseLeave={handleResume}
        onTouchStart={handlePause}
        onTouchEnd={handleResume}
        onPointerDown={handlePause}
        onPointerUp={handleResume}
        className="hide-scrollbar flex gap-4 overflow-x-auto pb-4 pt-2"
      >
        {loopCities.map((city, index) => (
          <CityCard
            key={`${city.id}-${index}`}
            city={city}
            index={index}
          />
        ))}
      </div>
    </div>
  )
}
